import { useEffect } from "react";
import {
    Flag,
    HandHeart,
    MapPin,
    MessageCircle,
    PhoneCall,
    ShieldCheck,
    UserCheck,
    Users,
} from "lucide-react";
import { Footer } from "@/components/lunar/Footer";
import { Logo } from "@/components/lunar/Logo";
import { Reveal } from "@/components/lunar/Reveal";

const SECTIONS = [
    {
        icon: MapPin,
        title: "Meeting in person",
        copy: "Meet in public or communal places, particularly when meeting someone for the first time. You never have to meet or continue an interaction if you don’t feel comfortable.",
    },
    {
        icon: UserCheck,
        title: "You’re in control",
        copy: "A Wave is private unless you both Wave. Messaging only becomes available once you’ve both chosen to connect. You can also disconnect, block or report another user.",
    },
    {
        icon: MessageCircle,
        title: "Made for real life",
        copy: "Lunar limits conversations to 10 messages because it’s designed to help people meet in person, rather than create lengthy conversations with strangers online. Use your judgement before meeting anyone.",
    },
    {
        icon: ShieldCheck,
        title: "Protect your information",
        copy: "Don’t share sensitive personal or financial information with someone you don’t know. You decide what information you share and when.",
    },
    {
        icon: Users,
        title: "Meeting for the first time",
        copy: "Where possible, stay in public or communal areas, tell someone where you’re going if appropriate, and trust your instincts. If something doesn’t feel right, leave the interaction.",
    },
    {
        icon: Flag,
        title: "Report a concern",
        copy: "If another user behaves inappropriately or makes you uncomfortable, please use Lunar’s block and report features.",
        link: {
            label: "Report a safety concern",
            href: "mailto:hello@somerhillgroup.com?subject=Lunar%20safety%20concern",
            testId: "safety-report-link",
        },
    },
    {
        icon: HandHeart,
        title: "Ask for Angela",
        copy: "Where a participating place operates Ask for Angela, users can discreetly ask staff for help by asking for “Angela”. Availability depends on the individual place; not every Lunar place participates.",
    },
];

export default function Safety() {
    useEffect(() => {
        document.title = "Safety — Lunar";
        return () => {
            document.title = "Lunar — The social network for real life";
        };
    }, []);

    return (
        <div data-testid="safety-page">
            <header className="mx-auto flex max-w-6xl items-center justify-between px-6 pt-7">
                <a href="/" aria-label="Back to Lunar home" data-testid="safety-back-home">
                    <Logo size={38} />
                </a>
                <a
                    href="/"
                    data-testid="safety-home-link"
                    className="rounded-full border border-[#C2C0B8] px-4 py-2 text-xs font-medium text-[#3E413B] transition-[border-color,color,transform] duration-300 hover:-translate-y-[1px] hover:border-[#899B6A] hover:text-[#2C3618]"
                >
                    Home
                </a>
            </header>

            <main className="mx-auto max-w-6xl px-6 pb-24 pt-16 sm:pt-20">
                <Reveal className="max-w-3xl">
                    <p className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#899B6A]">
                        Safety
                    </p>
                    <h1
                        data-testid="safety-heading"
                        className="font-display mt-5 text-4xl font-medium leading-[1.08] tracking-tight text-[#1D201A] sm:text-6xl"
                    >
                        Meeting people should feel safe.
                    </h1>
                    <p
                        data-testid="safety-intro"
                        className="mt-6 max-w-2xl text-base leading-relaxed text-[#3E413B] sm:text-lg"
                    >
                        Lunar is designed to help people connect in real life, while keeping you in
                        control of who you choose to interact with.
                    </p>
                </Reveal>

                <div data-testid="safety-sections" className="mt-14 grid gap-4 md:grid-cols-2">
                    {SECTIONS.map(({ icon: Icon, title, copy, link }, i) => (
                        <Reveal key={title} delay={i * 0.05}>
                            <section
                                data-testid={`safety-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                                className="h-full rounded-3xl border border-[#E5E3DC] bg-[#F9F8F4] p-7"
                            >
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#EEF1E5]">
                                    <Icon className="h-5 w-5 text-[#4A5D23]" strokeWidth={1.75} />
                                </div>
                                <h2 className="font-display mt-5 text-2xl font-semibold text-[#1D201A]">
                                    {title}
                                </h2>
                                <p className="mt-3 text-base leading-relaxed text-[#3E413B]">
                                    {copy}
                                </p>
                                {link && (
                                    <a
                                        href={link.href}
                                        data-testid={link.testId}
                                        className="mt-5 inline-flex rounded-full bg-[#2C3618] px-5 py-3 text-sm font-bold text-[#F9F8F4] transition-[background-color,transform] duration-300 hover:-translate-y-[1px] hover:bg-[#4A5D23]"
                                    >
                                        {link.label}
                                    </a>
                                )}
                            </section>
                        </Reveal>
                    ))}

                    <Reveal delay={0.35}>
                        <section
                            data-testid="safety-card-in-an-emergency"
                            className="rounded-3xl border border-[#C9D1B7] bg-[#EEF1E5] p-7 md:col-span-2"
                        >
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F9F8F4]">
                                <PhoneCall className="h-5 w-5 text-[#4A5D23]" strokeWidth={1.75} />
                            </div>
                            <h2 className="font-display mt-5 text-2xl font-semibold text-[#1D201A]">
                                In an emergency
                            </h2>
                            <p className="mt-3 max-w-3xl text-base leading-relaxed text-[#3E413B]">
                                Lunar is not an emergency service. If you or someone else is in
                                immediate danger, contact the local emergency services.
                            </p>
                        </section>
                    </Reveal>
                </div>
            </main>
            <Footer />
        </div>
    );
}
